import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    TextField, Button, Stack, Typography, Alert, Box, Autocomplete, CircularProgress,
    MenuItem, Checkbox, FormControlLabel, Chip, Divider, Card, CardActionArea, CardContent, Grid,
    ToggleButtonGroup, ToggleButton
} from '@mui/material';
import { crearTramitePension, getChecklistFormularios, getTramitesPension } from '../../services/pensionService';
import { buscarClientes, getClientePorCuil, getDomicilioDniCliente } from '../../services/clienteService';
import ModalRegistrarCliente from '../ModalRegistrarCliente';
import SeccionGenerarPacto from '../Pacto/SeccionGenerarPacto';
import usePactosPendientes from '../Pacto/usePactosPendientes';
import { useAuth } from '../../auth/AuthContext';
import { apellidoNombre } from '../../utils/nombrePersona';
import CampoFechaLiquidar from '../Tramite/CampoFechaLiquidar';
import PageHeader from '../ui/PageHeader';

// Las 3 modalidades que ve el usuario. Se guardan dos ejes (tipoPension, vinculoBeneficiario)
// de los que ellas se derivan: HIJO_DISCAPACIDAD fija el vínculo y pregunta si el causante
// estaba en actividad o jubilado (eso define tipoPension); DIRECTA/DERIVADA fijan tipoPension
// y preguntan el vínculo del beneficiario.
const MODALIDADES = [
    { value: 'DIRECTA', titulo: 'Pensión Directa', detalle: 'El causante estaba en actividad al fallecer', icono: 'bi-person-workspace' },
    { value: 'DERIVADA', titulo: 'Pensión Derivada', detalle: 'El causante ya estaba jubilado al fallecer', icono: 'bi-person-check' },
    { value: 'HIJO_DISCAPACIDAD', titulo: 'Pensión por Hijo con Discapacidad', detalle: 'El beneficiario es un hijo con CUD', icono: 'bi-heart-pulse' },
];

const VINCULOS = [
    { value: 'CONYUGE', label: 'Cónyuge' },
    { value: 'CONVIVIENTE', label: 'Conviviente' },
    { value: 'HIJO_MENOR', label: 'Hijo menor' },
];

const formularioVacio = (user) => ({
    idCliente: '',
        fechaInicioEstimada: '',
    idUsuario: user?.id || null,
    organismo: '',
    comentarios: '',

    modalidad: '',                 // DIRECTA | DERIVADA | HIJO_DISCAPACIDAD (solo UI)
    vinculoBeneficiario: '',       // CONYUGE | CONVIVIENTE | HIJO_MENOR | HIJO_CON_DISCAPACIDAD
    causanteEnActividad: '',       // solo HIJO_DISCAPACIDAD: 'SI' | 'NO' -> define tipoPension

    numeroBeneficio: '',
    causanteApellidoNombre: '',
    causanteDniCuil: '',
    causanteFechaNacimiento: '',
    causanteFechaDefuncion: '',
    causanteDomicilio: '',
    domicilioCausanteCoincide: null,   // null = sin evaluar, solo relevante para CONYUGE/CONVIVIENTE
    causanteRelacionDependencia: '',
    causanteAutonomoMonotributo: '',
    causanteCuentaAfip: '',

    aniosConvivencia: '',
    hijosReconocidosAmbos: false,
    cudVigente: false,
    fechaVencimientoCud: '',
    fechaCertificacionCud: '',
    acreditaDependenciaEconomica: false,
    fallecimientoAcreditadoAnses: false,
    observacionesCaratula: '',

    // Nulidad (V232): el cliente ya venía con este trámite denegado por ANSES (de otro estudio o
    // gestión propia previa) y arranca directo como reclamo de nulidad, en vez de "Ingreso del Trámite".
    esNulidad: false,
    fechaDenegatoria: '',
    motivoDenegatoria: '',
});

/** DIRECTA/DERIVADA lo fija la tarjeta elegida; en HIJO_DISCAPACIDAD lo define si el causante
 *  estaba en actividad o jubilado (ambas pensiones existen para un hijo con discapacidad). */
function tipoPensionDe(form) {
    if (form.modalidad === 'DIRECTA' || form.modalidad === 'DERIVADA') return form.modalidad;
    if (form.modalidad === 'HIJO_DISCAPACIDAD') {
        if (form.causanteEnActividad === 'SI') return 'DIRECTA';
        if (form.causanteEnActividad === 'NO') return 'DERIVADA';
    }
    return '';
}

function vinculoDe(form) {
    if (form.modalidad === 'HIJO_DISCAPACIDAD') return 'HIJO_CON_DISCAPACIDAD';
    return form.vinculoBeneficiario || '';
}

/**
 * Formulario para iniciar un trámite de Pensión. A diferencia de PUAM/PNC (planos), acá el
 * checklist es condicional: depende de tipoPension + vinculoBeneficiario (+ hijosReconocidosAmbos
 * en CONVIVIENTE), así que el preview se recalcula en vivo con cada selector (patrón PUAM/
 * Reconocimiento, no el de PNC que lo trae una sola vez).
 */
export default function FormularioIniciarTramitePension({ onSuccess, onCancel, cuilInicial, idTurnoOrigen }) {
    const navigate = useNavigate();
    const { registrarPacto, adjuntarATramite } = usePactosPendientes();
    const { user } = useAuth();
    const { cuil: cuilParam } = useParams();
    const cuilEfectivo = cuilInicial || cuilParam;

    const [form, setForm] = useState(formularioVacio(user));
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [guardando, setGuardando] = useState(false);

    // --- Búsqueda de cliente ---
    const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
    const [opcionesCliente, setOpcionesCliente] = useState([]);
    const [buscando, setBuscando] = useState(false);
    const [modalClienteOpen, setModalClienteOpen] = useState(false);
    const busquedaTimeoutRef = useRef(null);

    // --- Trámite de Pensión duplicado para el mismo cliente (patrón RTI/ART/Jubilación/PUAM) ---
    const [tieneDuplicado, setTieneDuplicado] = useState(false);
    const [cantidadDuplicados, setCantidadDuplicados] = useState(0);
    const [cargandoDuplicados, setCargandoDuplicados] = useState(false);
    const [confirmarDuplicado, setConfirmarDuplicado] = useState(false);

    // --- Checklist (condicional: se recalcula con cada selector, patrón PUAM/Reconocimiento) ---
    const [checklist, setChecklist] = useState([]);
    const [cargandoChecklist, setCargandoChecklist] = useState(false);

    // --- Domicilio DNI del beneficiario, de referencia para comparar con el del causante ---
    const [domicilioDniCliente, setDomicilioDniCliente] = useState(null);
    const [cargandoDomicilioDni, setCargandoDomicilioDni] = useState(false);

    const tipoPension = tipoPensionDe(form);
    const vinculo = vinculoDe(form);
    const requiereComparacionDomicilio = vinculo === 'CONYUGE' || vinculo === 'CONVIVIENTE';
    // Importa si el CUD estaba vigente EL DÍA del fallecimiento del causante, no hoy: certificado
    // antes (o el mismo día) y, si tiene vencimiento, no vencido antes de esa fecha. Que haya
    // vencido DESPUÉS del fallecimiento no importa (sin vencimiento = CUD permanente).
    const cudNoVigenteAlFallecimiento = vinculo === 'HIJO_CON_DISCAPACIDAD'
        && !!form.fechaCertificacionCud && !!form.causanteFechaDefuncion
        && (form.fechaCertificacionCud > form.causanteFechaDefuncion
            || (!!form.fechaVencimientoCud && form.fechaVencimientoCud < form.causanteFechaDefuncion));

    // Carga inicial de cliente si viene por URL
    useEffect(() => {
        const cargar = async () => {
            if (!cuilEfectivo) return;
            try {
                const cliente = await getClientePorCuil(cuilEfectivo);
                setClienteSeleccionado(cliente);
                setForm(prev => ({ ...prev, idCliente: cliente.cuil }));
            } catch (err) {
                console.error('Error al cargar cliente inicial:', err);
            }
        };
        cargar();
    }, [cuilEfectivo]);

    // Preview del checklist: se recalcula con cada cambio de clasificación.
    useEffect(() => {
        if (!tipoPension || !vinculo) {
            setChecklist([]);
            return;
        }
        let activo = true;
        const cargarChecklist = async () => {
            setCargandoChecklist(true);
            try {
                const data = await getChecklistFormularios(
                    tipoPension, vinculo, form.hijosReconocidosAmbos, form.domicilioCausanteCoincide,
                    form.causanteFechaDefuncion, form.fechaCertificacionCud, form.fechaVencimientoCud);
                if (activo) setChecklist(Array.isArray(data) ? data : []);
            } catch (err) {
                console.error('Error al previsualizar checklist:', err);
                if (activo) setChecklist([]);
            } finally {
                if (activo) setCargandoChecklist(false);
            }
        };
        cargarChecklist();
        return () => { activo = false; };
    }, [tipoPension, vinculo, form.hijosReconocidosAmbos, form.domicilioCausanteCoincide, form.causanteFechaDefuncion, form.fechaCertificacionCud, form.fechaVencimientoCud]);

    // Domicilio DNI del beneficiario: solo aplica a CONYUGE/CONVIVIENTE (comparación con el
    // domicilio del causante). Si el vínculo cambia y deja de aplicar, se resetea la confirmación
    // ya hecha para no arrastrar un "no coincide" que ya no tiene sentido.
    useEffect(() => {
        if (!form.idCliente || !requiereComparacionDomicilio) {
            setDomicilioDniCliente(null);
            if (form.domicilioCausanteCoincide !== null) {
                setForm(prev => ({ ...prev, domicilioCausanteCoincide: null }));
            }
            return;
        }
        let activo = true;
        const cargarDomicilio = async () => {
            setCargandoDomicilioDni(true);
            try {
                const data = await getDomicilioDniCliente(form.idCliente);
                if (activo) setDomicilioDniCliente(data?.domicilioFormateado || null);
            } catch (err) {
                console.error('Error al obtener el domicilio DNI del cliente:', err);
                if (activo) setDomicilioDniCliente(null);
            } finally {
                if (activo) setCargandoDomicilioDni(false);
            }
        };
        cargarDomicilio();
        return () => { activo = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form.idCliente, requiereComparacionDomicilio]);

    // Trámite de Pensión ya existente para el cliente seleccionado.
    useEffect(() => {
        const cuilCliente = form.idCliente;
        if (!cuilCliente) {
            setTieneDuplicado(false);
            setCantidadDuplicados(0);
            setConfirmarDuplicado(false);
            return;
        }

        let activo = true;
        const verificarDuplicado = async () => {
            setCargandoDuplicados(true);
            try {
                const tramites = await getTramitesPension(cuilCliente);
                const items = Array.isArray(tramites) ? tramites : [];
                if (activo) {
                    setCantidadDuplicados(items.length);
                    setTieneDuplicado(items.length > 0);
                    if (items.length === 0) {
                        setConfirmarDuplicado(false);
                    }
                }
            } catch (err) {
                console.error('Error validando trámites de Pensión duplicados:', err);
                if (activo) {
                    setTieneDuplicado(false);
                    setCantidadDuplicados(0);
                }
            } finally {
                if (activo) setCargandoDuplicados(false);
            }
        };

        verificarDuplicado();
        return () => { activo = false; };
    }, [form.idCliente]);

    const handleBusquedaCliente = (_event, value) => {
        if (!value) { setOpcionesCliente([]); setBuscando(false); return; }
        setBuscando(true);
        if (busquedaTimeoutRef.current) clearTimeout(busquedaTimeoutRef.current);
        busquedaTimeoutRef.current = setTimeout(async () => {
            try {
                const resultados = await buscarClientes(value);
                setOpcionesCliente(resultados);
            } catch (err) {
                console.error('Error al buscar clientes', err);
            } finally {
                setBuscando(false);
            }
        }, 300);
    };

    const handleSeleccionCliente = (_event, newValue) => {
        setClienteSeleccionado(newValue);
        setForm(prev => ({ ...prev, idCliente: newValue ? newValue.cuil : '' }));
    };

    const handleRegistroExitoso = (clienteNuevo) => {
        setClienteSeleccionado(clienteNuevo);
        setForm(prev => ({ ...prev, idCliente: clienteNuevo.cuil }));
        setModalClienteOpen(false);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const handleCheck = (e) => {
        const { name, checked } = e.target;
        setForm(prev => ({ ...prev, [name]: checked }));
    };

    const seleccionarModalidad = (modalidad) => {
        // Cambiar de modalidad invalida las preguntas que dependían de la anterior.
        setForm(prev => ({
            ...prev,
            modalidad,
            vinculoBeneficiario: modalidad === 'HIJO_DISCAPACIDAD' ? '' : prev.vinculoBeneficiario,
            causanteEnActividad: modalidad === 'HIJO_DISCAPACIDAD' ? prev.causanteEnActividad : '',
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.idCliente) { setError('Debe seleccionar un cliente.'); return; }
        if (!form.modalidad) { setError('Debe elegir la modalidad de la pensión.'); return; }
        if (!tipoPension) { setError('Falta indicar si el causante estaba en actividad o jubilado.'); return; }
        if (!vinculo) { setError('Debe indicar el vínculo del beneficiario con el causante.'); return; }
        if (tieneDuplicado && !confirmarDuplicado) {
            setError('Debe confirmar que desea crear otro trámite de Pensión para un cliente que ya tiene uno.');
            return;
        }
        setError('');
        setSuccess('');
        setGuardando(true);
        try {
            const payload = {
                idCliente: form.idCliente,
                idUsuario: form.idUsuario,
                organismo: form.organismo || null,
                comentarios: form.comentarios,
                tipoPension,
                vinculoBeneficiario: vinculo,
                numeroBeneficio: form.numeroBeneficio || null,
                causanteApellidoNombre: form.causanteApellidoNombre || null,
                causanteDniCuil: form.causanteDniCuil || null,
                causanteFechaNacimiento: form.causanteFechaNacimiento || null,
                causanteFechaDefuncion: form.causanteFechaDefuncion || null,
                causanteDomicilio: form.causanteDomicilio || null,
                domicilioCausanteCoincide: form.domicilioCausanteCoincide,
                causanteRelacionDependencia: form.causanteRelacionDependencia || null,
                causanteAutonomoMonotributo: form.causanteAutonomoMonotributo || null,
                causanteCuentaAfip: form.causanteCuentaAfip || null,
                aniosConvivencia: form.aniosConvivencia === '' ? null : Number(form.aniosConvivencia),
                hijosReconocidosAmbos: form.hijosReconocidosAmbos,
                cudVigente: form.cudVigente,
                fechaVencimientoCud: form.fechaVencimientoCud || null,
                fechaCertificacionCud: form.fechaCertificacionCud || null,
                acreditaDependenciaEconomica: form.acreditaDependenciaEconomica,
                fallecimientoAcreditadoAnses: form.fallecimientoAcreditadoAnses,
                observacionesCaratula: form.observacionesCaratula || null,
                fechaInicioEstimada: form.fechaInicioEstimada || null,
                idTurnoOrigen: idTurnoOrigen || null,
                esNulidad: form.esNulidad,
                fechaDenegatoria: form.esNulidad ? (form.fechaDenegatoria || null) : null,
                motivoDenegatoria: form.esNulidad ? (form.motivoDenegatoria || null) : null,
            };
            const creado = await crearTramitePension(payload);
            // El pacto se emitió antes de que el trámite existiera: recién ahora se puede adjuntar.
            await adjuntarATramite(creado.idTramite);
            setSuccess(`Trámite de Pensión creado con éxito. ID: ${creado.idTramite}`);
            if (onSuccess) {
                onSuccess(creado);
            } else {
                navigate(`/pension/tramites/${creado.idTramite}`);
            }
        } catch (err) {
            setError(err.response?.data || err.response?.data?.message || 'Error al crear el trámite.');
        } finally {
            setGuardando(false);
        }
    };

    const seccionTitulo = (icono, texto) => (
        <div style={{
            background: 'var(--primary-bg)',
            padding: '0.75rem 1.25rem', borderRadius: 'var(--radius-lg)', border: '1px solid var(--primary-border)', marginTop: '0.5rem'
        }}>
            <Typography variant="h6" fontWeight={600} sx={{ color: 'var(--primary-text)', fontSize: 'var(--fs-texto-lg)' }}>
                <i className={`bi ${icono} me-2`}></i>{texto}
            </Typography>
        </div>
    );

    return (
        <div className="area-rti">
            <Box sx={{ background: 'var(--bg-page)', minHeight: '100vh', padding: 3 }}>
                <Box sx={{ maxWidth: 900, margin: 'auto', background: 'white', borderRadius: '16px', boxShadow: 'var(--shadow-md)', overflow: 'hidden' }}>
                    <PageHeader
                        variante="banner"
                        icono="people"
                        titulo="Iniciar Trámite de Pensión"
                        descripcion="Complete los datos para registrar una nueva Pensión"
                    />

                    <Box sx={{ padding: '2rem' }}>
                    <ModalRegistrarCliente open={modalClienteOpen} onClose={() => setModalClienteOpen(false)} onSuccess={handleRegistroExitoso} mostrarDialogoConfirmacion={false} />

                    <form onSubmit={handleSubmit}>
                        <Stack spacing={3}>
                            {/* Cliente */}
                            {seccionTitulo('bi-person-check', 'Seleccionar Cliente')}
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                                <Autocomplete
                                    id="buscar-cliente-pension"
                                    options={opcionesCliente}
                                    getOptionLabel={(option) => `${apellidoNombre(option.apellido, option.nombre)} (${option.cuil})`}
                                    onInputChange={handleBusquedaCliente}
                                    onChange={handleSeleccionCliente}
                                    value={clienteSeleccionado}
                                    isOptionEqualToValue={(option, value) => option.cuil === value.cuil}
                                    noOptionsText="No se encontraron clientes"
                                    loading={buscando}
                                    loadingText="Buscando..."
                                    sx={{ flexGrow: 1 }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Buscar cliente por CUIL, Apellido o Nombre"
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <>
                                                        {buscando ? <CircularProgress color="inherit" size={20} /> : null}
                                                        {params.InputProps.endAdornment}
                                                    </>
                                                ),
                                            }}
                                        />
                                    )}
                                />
                                <Button variant="outlined" onClick={() => setModalClienteOpen(true)} sx={{ height: '56px', flexShrink: 0 }}>
                                    Registrar Cliente
                                </Button>
                            </Box>

                            <SeccionGenerarPacto cliente={clienteSeleccionado} onPactoGenerado={registrarPacto} />

                            {/* Nulidad: el cliente ya venía con este trámite denegado (de otro estudio o
                                gestión propia previa) */}
                            <Box sx={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '12px', p: 2 }}>
                                <FormControlLabel
                                    control={<Checkbox name="esNulidad" checked={form.esNulidad} onChange={handleCheck} />}
                                    label="Este cliente ya tenía este trámite de Pensión denegado por ANSES (nulidad)"
                                />
                                {form.esNulidad && (
                                    <Stack spacing={2} sx={{ mt: 1 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            El trámite arranca directo como reclamo de nulidad, con los mismos datos
                                            y checklist que cualquier Pensión.
                                        </Typography>
                                        <TextField
                                            label="Fecha de la denegatoria"
                                            name="fechaDenegatoria"
                                            type="date"
                                            value={form.fechaDenegatoria}
                                            onChange={handleChange}
                                            fullWidth
                                            InputLabelProps={{ shrink: true }}
                                        />
                                        <TextField
                                            label="Motivo de la denegatoria"
                                            name="motivoDenegatoria"
                                            value={form.motivoDenegatoria}
                                            onChange={handleChange}
                                            fullWidth
                                            helperText='Por ejemplo: "falta de acreditación de convivencia".'
                                        />
                                    </Stack>
                                )}
                            </Box>

                            {/* Modalidad */}
                            {seccionTitulo('bi-signpost-split', 'Modalidad de la Pensión')}
                            <Grid container spacing={2}>
                                {MODALIDADES.map((m) => (
                                    <Grid key={m.value} size={{ xs: 12, sm: 4 }}>
                                        <Card
                                            variant="outlined"
                                            sx={{
                                                borderRadius: '12px',
                                                borderColor: form.modalidad === m.value ? '#7c3aed' : '#e5e7eb',
                                                borderWidth: form.modalidad === m.value ? 2 : 1,
                                                background: form.modalidad === m.value ? '#f5f3ff' : 'white',
                                                height: '100%',
                                            }}
                                        >
                                            <CardActionArea onClick={() => seleccionarModalidad(m.value)} sx={{ height: '100%', p: 1 }}>
                                                <CardContent>
                                                    <i className={`bi ${m.icono}`} style={{ fontSize: '1.5rem', color: '#7c3aed' }}></i>
                                                    <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 1 }}>{m.titulo}</Typography>
                                                    <Typography variant="body2" color="text.secondary">{m.detalle}</Typography>
                                                </CardContent>
                                            </CardActionArea>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>

                            {/* Según la modalidad: vínculo (Directa/Derivada) o si el causante estaba en actividad (Hijo con discapacidad) */}
                            {(form.modalidad === 'DIRECTA' || form.modalidad === 'DERIVADA') && (
                                <TextField
                                    select
                                    label="Vínculo del beneficiario con el causante"
                                    name="vinculoBeneficiario"
                                    value={form.vinculoBeneficiario}
                                    onChange={handleChange}
                                    fullWidth
                                    required
                                >
                                    {VINCULOS.map((v) => (
                                        <MenuItem key={v.value} value={v.value}>{v.label}</MenuItem>
                                    ))}
                                </TextField>
                            )}
                            {form.modalidad === 'HIJO_DISCAPACIDAD' && (
                                <TextField
                                    select
                                    label="¿El causante estaba en actividad o ya jubilado al fallecer?"
                                    name="causanteEnActividad"
                                    value={form.causanteEnActividad}
                                    onChange={handleChange}
                                    fullWidth
                                    required
                                    helperText="Define si la documentación es de Pensión Directa o Derivada."
                                >
                                    <MenuItem value="SI">En actividad (Pensión Directa)</MenuItem>
                                    <MenuItem value="NO">Ya jubilado (Pensión Derivada)</MenuItem>
                                </TextField>
                            )}

                            {tipoPension === 'DERIVADA' && (
                                <TextField
                                    label="N° de Beneficio del causante"
                                    name="numeroBeneficio"
                                    value={form.numeroBeneficio}
                                    onChange={handleChange}
                                    fullWidth
                                    helperText="N° de prestación del jubilado fallecido."
                                />
                            )}

                            {/* Datos del causante */}
                            {seccionTitulo('bi-person-x', 'Datos del Causante')}
                            <TextField label="Apellido y Nombre del causante" name="causanteApellidoNombre" value={form.causanteApellidoNombre} onChange={handleChange} fullWidth />
                            <TextField label="DNI/CUIL del causante" name="causanteDniCuil" value={form.causanteDniCuil} onChange={handleChange} fullWidth />
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                <TextField label="Fecha de nacimiento" name="causanteFechaNacimiento" type="date" value={form.causanteFechaNacimiento} onChange={handleChange} fullWidth InputLabelProps={{ shrink: true }} />
                                <TextField label="Fecha de defunción" name="causanteFechaDefuncion" type="date" value={form.causanteFechaDefuncion} onChange={handleChange} fullWidth InputLabelProps={{ shrink: true }} />
                            </Stack>
                            <TextField label="Domicilio del causante" name="causanteDomicilio" value={form.causanteDomicilio} onChange={handleChange} fullWidth helperText="Tal como figura en el acta de defunción." />

                            {requiereComparacionDomicilio && (
                                <Box sx={{ background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: '12px', p: 2 }}>
                                    <Typography variant="subtitle2" fontWeight={600} color="#5b21b6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        <i className="bi bi-geo-alt"></i>
                                        Domicilio DNI del beneficiario
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                        {cargandoDomicilioDni
                                            ? 'Buscando...'
                                            : (domicilioDniCliente || 'El cliente no tiene domicilio DNI cargado.')}
                                    </Typography>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        ¿El domicilio del causante coincide con el del beneficiario?
                                    </Typography>
                                    <ToggleButtonGroup
                                        exclusive
                                        size="small"
                                        value={form.domicilioCausanteCoincide}
                                        onChange={(_e, valor) => setForm(prev => ({ ...prev, domicilioCausanteCoincide: valor }))}
                                        sx={{
                                            '& .MuiToggleButton-root': { textTransform: 'none', px: 2, borderColor: '#ddd6fe' },
                                            '& .MuiToggleButton-root.Mui-selected': {
                                                backgroundColor: '#7c3aed', color: 'white',
                                                '&:hover': { backgroundColor: '#6d28d9' },
                                            },
                                        }}
                                    >
                                        <ToggleButton value={true}>Coincide</ToggleButton>
                                        <ToggleButton value={false}>No coincide</ToggleButton>
                                    </ToggleButtonGroup>
                                </Box>
                            )}
                            {form.domicilioCausanteCoincide === false && (
                                <Alert severity="warning" sx={{ borderRadius: '10px' }}>
                                    Los domicilios no coinciden: ANSES no reconoce la convivencia por presunción, así
                                    que además de la partida de matrimonio el trámite va a requerir acreditar
                                    convivencia (Prueba de convivencia).
                                </Alert>
                            )}

                            <TextField label="Relación de dependencia" name="causanteRelacionDependencia" value={form.causanteRelacionDependencia} onChange={handleChange} fullWidth helperText='Por ejemplo: "4 años y 10 meses".' />
                            <TextField select label="Autónomo o monotributo" name="causanteAutonomoMonotributo" value={form.causanteAutonomoMonotributo} onChange={handleChange} fullWidth>
                                <MenuItem value=""><em>Sin especificar</em></MenuItem>
                                <MenuItem value="AUTONOMO">Autónomo</MenuItem>
                                <MenuItem value="MONOTRIBUTO">Monotributo</MenuItem>
                            </TextField>
                            <TextField label="Cuenta AFIP" name="causanteCuentaAfip" value={form.causanteCuentaAfip} onChange={handleChange} fullWidth />

                            <FormControlLabel
                                control={<Checkbox name="fallecimientoAcreditadoAnses" checked={form.fallecimientoAcreditadoAnses} onChange={handleCheck} />}
                                label="El fallecimiento ya está acreditado en Mi ANSES"
                            />
                            {!form.fallecimientoAcreditadoAnses && (
                                <Alert severity="warning" sx={{ borderRadius: '10px' }}>
                                    El fallecimiento todavía no figura acreditado en Mi ANSES. No impide cargar el
                                    trámite; el operador decide si continúa igualmente.
                                </Alert>
                            )}

                            {/* Acreditación del vínculo, según corresponda. Para CÓNYUGE solo aplica si el
                                domicilio no coincide: ahí ANSES exige lo mismo que a un CONVIVIENTE. */}
                            {(vinculo === 'CONVIVIENTE' || (vinculo === 'CONYUGE' && form.domicilioCausanteCoincide === false)) && (
                                <>
                                    {seccionTitulo('bi-house-heart', 'Acreditación de la Convivencia')}
                                    <TextField
                                        label="Años de convivencia"
                                        name="aniosConvivencia"
                                        type="number"
                                        value={form.aniosConvivencia}
                                        onChange={handleChange}
                                        fullWidth
                                        helperText="Se exigen 5 años, o 2 si hay hijos reconocidos por ambos."
                                    />
                                    <FormControlLabel
                                        control={<Checkbox name="hijosReconocidosAmbos" checked={form.hijosReconocidosAmbos} onChange={handleCheck} />}
                                        label="Hay hijos en común reconocidos por ambos"
                                    />
                                </>
                            )}
                            {vinculo === 'HIJO_CON_DISCAPACIDAD' && (
                                <>
                                    {seccionTitulo('bi-file-medical', 'Acreditación de la Discapacidad')}
                                    <TextField
                                        label="Fecha de certificación del CUD"
                                        name="fechaCertificacionCud"
                                        type="date"
                                        value={form.fechaCertificacionCud}
                                        onChange={handleChange}
                                        fullWidth
                                        InputLabelProps={{ shrink: true }}
                                        helperText="Tiene que ser anterior o igual al fallecimiento del causante."
                                    />
                                    <TextField
                                        label="Fecha de vencimiento del CUD"
                                        name="fechaVencimientoCud"
                                        type="date"
                                        value={form.fechaVencimientoCud}
                                        onChange={handleChange}
                                        fullWidth
                                        InputLabelProps={{ shrink: true }}
                                        helperText="Dejar vacío si el CUD no vence (permanente). Tiene que ser posterior o igual al fallecimiento del causante."
                                    />
                                    {cudNoVigenteAlFallecimiento && (
                                        <Alert severity="error" sx={{ borderRadius: '10px' }}>
                                            El CUD no estaba vigente el día del fallecimiento del causante (se certificó
                                            después, o ya había vencido antes): no acredita la discapacidad al momento
                                            del deceso. El trámite va a requerir estudios y certificados médicos de la
                                            incapacidad a esa fecha.
                                        </Alert>
                                    )}
                                    <FormControlLabel
                                        control={<Checkbox name="cudVigente" checked={form.cudVigente} onChange={handleCheck} />}
                                        label="Tiene CUD vigente en la actualidad"
                                    />
                                    <FormControlLabel
                                        control={<Checkbox name="acreditaDependenciaEconomica" checked={form.acreditaDependenciaEconomica} onChange={handleCheck} />}
                                        label="Acredita dependencia económica del titular"
                                    />
                                </>
                            )}

                            {/* Observaciones */}
                            {seccionTitulo('bi-file-earmark-text', 'Datos del Trámite')}
                            <TextField select label="Organismo" name="organismo" value={form.organismo} onChange={handleChange} fullWidth
                                helperText="Seleccione si el trámite se gestiona ante Provincia o ANSES.">
                                <MenuItem value=""><em>Sin especificar</em></MenuItem>
                                <MenuItem value="ANSES">ANSES</MenuItem>
                                <MenuItem value="PROVINCIA">Provincia</MenuItem>
                            </TextField>
                            <TextField label="Observaciones de la carátula" name="observacionesCaratula" value={form.observacionesCaratula} onChange={handleChange} multiline rows={3} fullWidth
                                helperText="Se imprime en la carátula. Se puede completar después desde &quot;Generar Carátula&quot;." />
                            <CampoFechaLiquidar
                                valor={form.fechaInicioEstimada}
                                onChange={(v) => setForm((f) => ({ ...f, fechaInicioEstimada: v }))}
                            />

                            <TextField label="Comentarios" name="comentarios" value={form.comentarios} onChange={handleChange} multiline rows={3} fullWidth
                                helperText="Observaciones libres sobre el trámite." />

                            {/* Checklist (condicional: se recalcula con cada selector) */}
                            <Box sx={{ background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: '12px', p: 2 }}>
                                <Typography variant="subtitle1" fontWeight={600} color="#5b21b6" sx={{ mb: 1 }}>
                                    <i className="bi bi-check2-square me-2"></i>
                                    Formularios requeridos {cargandoChecklist && <CircularProgress size={14} sx={{ ml: 1 }} />}
                                </Typography>
                                <Divider sx={{ mb: 1 }} />
                                {!tipoPension || !vinculo ? (
                                    <Typography variant="body2" color="text.secondary">Elegí la modalidad para ver los formularios requeridos.</Typography>
                                ) : checklist.length === 0 && !cargandoChecklist ? (
                                    <Typography variant="body2" color="text.secondary">Sin formularios configurados.</Typography>
                                ) : (
                                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                        {checklist.map(f => (
                                            <Chip key={f.codigo} label={f.nombre} size="small" color="secondary" variant="outlined" />
                                        ))}
                                    </Stack>
                                )}
                            </Box>

                            {error && <Alert severity="error" sx={{ borderRadius: '10px' }}>{String(error)}</Alert>}
                            {success && <Alert severity="success" sx={{ borderRadius: '10px' }}>{success}</Alert>}
                            {tieneDuplicado && (
                                <Alert severity="warning" sx={{ borderRadius: '10px' }}>
                                    Este cliente ya tiene {cantidadDuplicados === 1 ? 'un trámite de Pensión' : `${cantidadDuplicados} trámites de Pensión`}. ¿Está seguro de que desea crear otro?
                                </Alert>
                            )}
                            {tieneDuplicado && (
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={confirmarDuplicado}
                                            onChange={(e) => setConfirmarDuplicado(e.target.checked)}
                                        />
                                    }
                                    label="Confirmo que deseo continuar con un nuevo trámite de Pensión igualmente."
                                />
                            )}
                            {cargandoDuplicados && <Alert severity="info" sx={{ borderRadius: '10px' }}>Verificando trámites de Pensión activos del cliente...</Alert>}

                            <Stack direction="row" spacing={2} sx={{ width: '100%', marginTop: '1rem' }}>
                                <Button fullWidth variant="outlined" size="large"
                                    onClick={() => onCancel ? onCancel() : navigate('/tramites')}
                                    sx={{ color: '#dc2626', border: '2px solid #fecaca', borderRadius: '10px', textTransform: 'none' }}>
                                    <i className="bi bi-x-circle me-2"></i>Cancelar
                                </Button>
                                <Button fullWidth type="submit" size="large" disabled={!form.idCliente || guardando}
                                    sx={{
                                        background: 'var(--primary)', color: 'white', borderRadius: '10px',
                                        textTransform: 'none', fontWeight: 600,
                                        '&:hover': { background: 'var(--primary)' },
                                        '&:disabled': { background: '#e5e7eb', color: '#9ca3af' }
                                    }}>
                                    {guardando ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <><i className="bi bi-check-circle me-2"></i>Iniciar Trámite</>}
                                </Button>
                            </Stack>
                        </Stack>
                    </form>
                </Box>
            </Box>
        </Box>
    </div>
);
}
